import { Link } from '@tanstack/react-router';
import { Heart } from '@/core/ui/icons';
import { cn } from '@/core/ui';
import type { Product } from '../domain/product';
import { ProductImage } from './ProductImage';

interface Props {
  product: Product;
  favorite: boolean;
  selected: boolean;
  onToggleFavorite: () => void;
}

export function ProductCard({ product, favorite, selected, onToggleFavorite }: Props) {
  return (
    <article className="group">
      <div className="mb-1.5 flex h-6 items-center justify-between">
        {product.bestSeller ? (
          <span className="rounded-sm bg-brand-400 px-1.5 py-0.5 text-[0.65rem] font-semibold text-on-brand">
            Os mais vendidos
          </span>
        ) : (
          <span />
        )}
        <button
          type="button"
          onClick={onToggleFavorite}
          aria-pressed={favorite}
          aria-label={favorite ? `Remover ${product.name} dos favoritos` : `Favoritar ${product.name}`}
          className="rounded-md p-1 text-fg-muted transition hover:text-fg"
        >
          <Heart
            aria-hidden
            weight={favorite ? 'fill' : 'duotone'}
            className={cn('size-4', favorite && 'text-brand-400')}
          />
        </button>
      </div>

      {/* O produto selecionado vai para a URL: o link é compartilhável e o
          "voltar" do navegador fecha o painel. */}
      <Link
        to="/catalogo"
        search={(prev) => ({ ...prev, produto: product.slug })}
        aria-current={selected || undefined}
        // Selecionado e hover pelo tom do fundo do cartão — sem contorno.
        className={cn(
          '-m-2 block rounded-3xl p-2 transition-colors',
          selected ? 'bg-brand-400/15' : 'hover:bg-surface-2/70',
        )}
      >
        {/* Fundo branco de propósito: é o "estúdio" da foto do produto. No
            claro, um degrau de cinza separa o cartão da página, e a foto
            "multiplica" para o branco dela sumir no tom. */}
        <div className="flex aspect-[16/9] items-center justify-center overflow-hidden rounded-2xl bg-white p-2 light:bg-surface-2">
          {product.imageUrl ? (
            <img
              src={product.imageUrl}
              alt=""
              className="h-full w-auto object-contain light:mix-blend-multiply"
              loading="lazy"
            />
          ) : (
            <ProductImage packaging={product.packaging} brand={product.brand} />
          )}
        </div>

        <h3 className="mt-2.5 px-1 text-sm font-semibold">{product.name}</h3>
        <p className="px-1 text-xs text-fg-subtle">{product.summary}</p>
      </Link>
    </article>
  );
}
